import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { CalculationType, CalculatorField, CalculatorFieldTypeDB, CalculatorFieldTypeUI, CalculatorInput, CalculatorOptionType, CalculatorPadOptionDB, getAllPadOptions } from '../../admin/calculator/store';
import { Scrollable } from '../scrollable/Scrollable';
import { CustomTooltip } from '../tooltip';
import styles from './Calculator.module.scss';
import { CalculatorPadOption } from './CalculatorPadOption';
import { LegacyCalculatorFieldType, LegacyCalculatorInputType } from './constants';

const { grey } = styles;

interface CalculatorInputsProps {
    calculatorFieldType: CalculatorFieldTypeDB | CalculatorFieldTypeUI;
    selectedCalculation: CalculatorField;
    selectedBuilderIndex: number | null;
    selectedCalculationBuilder: CalculationType;
    addOption: (value: CalculatorInput) => void;
    addNewCalculation: () => void;
    addNewBuilder: () => void;
    disabled: boolean;
    width: number;
    maxHeight: number;
    showOutputField: boolean;
    includeFreeTextOption: boolean;
}

export const CalculatorInputs: React.FC<CalculatorInputsProps> = ({
    calculatorFieldType,
    selectedCalculation,
    selectedBuilderIndex,
    selectedCalculationBuilder,
    addOption,
    addNewCalculation,
    addNewBuilder,
    disabled,
    width,
    maxHeight,
    includeFreeTextOption
}) => {
    const allPadOptions = useAppSelector(getAllPadOptions);

    const availableForBuilder = useMemo(() => calculatorFieldType.buildPadOptions.map(id => allPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId === id)).filter(option => !isUndefined(option)) as CalculatorPadOptionDB[], [calculatorFieldType, allPadOptions]);
    const availableForOutput = useMemo(() => calculatorFieldType.outputPadOptions.map(id => allPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId === id)).filter(option => !isUndefined(option)) as CalculatorPadOptionDB[], [calculatorFieldType, allPadOptions]);
    const availableForGracePeriod = useMemo(() => calculatorFieldType.gracePeriodPadOptions.map(id => allPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId === id)).filter(option => !isUndefined(option)) as CalculatorPadOptionDB[], [calculatorFieldType, allPadOptions]);
    const availableInputFields = useMemo(() => {
        if (isNull(selectedBuilderIndex)) {
            return selectedCalculationBuilder === CalculationType.OUTPUT ? availableForOutput : availableForGracePeriod;
        }
        return availableForBuilder;
    }, [selectedBuilderIndex, availableForBuilder, selectedCalculationBuilder, availableForGracePeriod, availableForOutput]);

    const availableInputs = useMemo(() => {
        if (!calculatorFieldType.includeOther && !includeFreeTextOption) {
            return availableInputFields.filter(({ type }) => type !== CalculatorOptionType.TEXT);
        }
        return availableInputFields;
    }, [availableInputFields, calculatorFieldType.includeOther, includeFreeTextOption]);

    const selectedBuilder = useMemo(() => selectedCalculation.calculation.find(({ index }) => index === selectedBuilderIndex)?.builder || [], [selectedCalculation, selectedBuilderIndex]);

    const getRatingsLabel = useCallback((ratingsLabel: string) => {
        let label = ratingsLabel;
        if (ratingsLabel.includes('LT')) {
            label = label.replace('LT', 'L-T Rating');
        }
        if (ratingsLabel.includes('ST')) {
            label = label.replace('ST', 'S-T Rating');
        }
        return label;
    }, []);

    const getRatingsTriggersInputDisabled = useCallback((input: CalculatorPadOptionDB) => {
        const secondLatestOutput = selectedBuilder.length > 1 ? selectedBuilder[selectedBuilder.length - 2] : null;
        const latestOutput = selectedBuilder[selectedBuilder.length - 1];
        if (isUndefined(latestOutput)) {
            return false;
        }
        if (latestOutput.type === LegacyCalculatorInputType.RATING_TYPE && !([LegacyCalculatorInputType.EQUALS, LegacyCalculatorInputType.COMPARATOR] as string[]).includes(input.inputType)) {
            return true;
        }
        if (selectedBuilder.map(({ type }) => type).includes(LegacyCalculatorInputType.LOGICAL_OPERATOR) && input.inputType === LegacyCalculatorInputType.LOGICAL_OPERATOR) {
            return true;
        }
        if (secondLatestOutput?.type === LegacyCalculatorInputType.RATING_TYPE && ([LegacyCalculatorInputType.COMPARATOR, LegacyCalculatorInputType.EQUALS] as string[]).includes(latestOutput.type)) {
            return input.label !== (getRatingsLabel(secondLatestOutput.value));
        }
        return false;
    }, [selectedBuilder, getRatingsLabel]);

    const getMaturityInputDisabled = useCallback((input: CalculatorPadOptionDB) => {
        const latestOutput = selectedBuilder[selectedBuilder.length - 1];
        if (isUndefined(latestOutput)) {
            return !([LegacyCalculatorInputType.MATURITY, LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL, LegacyCalculatorInputType.NUMBER] as string[]).includes(input.inputType);
        }
        if (
            (selectedBuilder[0].type === LegacyCalculatorInputType.MATURITY && selectedBuilder.length === 4) ||
            (([LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL] as string[]).includes(selectedBuilder[0].type) && selectedBuilder.length === 5) ||
            (selectedBuilder.filter(({ type }) => ([LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL] as string[]).includes(type)).length === 0 && selectedBuilder.length === 7) ||
            (selectedBuilder.filter(({ type }) => ([LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL] as string[]).includes(type)).length === 1 && selectedBuilder.length === 8)
        ) {
            return true;
        }
        if (latestOutput.type === LegacyCalculatorInputType.NUMBER) {
            return input.inputType !== LegacyCalculatorInputType.MATURITY_PERIOD;
        }
        if (([LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL] as string[]).includes(latestOutput.type)) {
            return input.inputType !== LegacyCalculatorInputType.MATURITY;
        }
        if (([LegacyCalculatorInputType.MATURITY, LegacyCalculatorInputType.MATURITY_PERIOD] as string[]).includes(latestOutput.type)) {
            const previousComparator = selectedBuilder.find(({ type }) => type === LegacyCalculatorInputType.COMPARATOR);
            if (!previousComparator) {
                return input.inputType !== LegacyCalculatorInputType.COMPARATOR;
            }
            const previousComparatorWasPrevious = ['<', '<='].includes(previousComparator.value);
            return previousComparatorWasPrevious ?
                !['<', '<='].includes(input.label) :
                !['>', '>='].includes(input.label);
        }
        if (latestOutput.type === LegacyCalculatorInputType.COMPARATOR && selectedBuilder.length >= 2) {
            const secondLatestOutput = selectedBuilder[selectedBuilder.length - 2];
            return secondLatestOutput.type === LegacyCalculatorInputType.MATURITY ?
                input.inputType !== LegacyCalculatorInputType.NUMBER :
                !([LegacyCalculatorInputType.MATURITY, LegacyCalculatorInputType.ORIGINAL, LegacyCalculatorInputType.RESIDUAL] as string[]).includes(input.inputType);
        }
        return false;
    }, [selectedBuilder]);

    const getInputDisabled = useMemo(() => {
        if (calculatorFieldType.name === LegacyCalculatorFieldType.RATINGS_TRIGGERS) {
            return getRatingsTriggersInputDisabled;
        }
        if (calculatorFieldType.name === LegacyCalculatorFieldType.MATURITY) {
            return getMaturityInputDisabled;
        }
        return () => false;
    }, [calculatorFieldType.name, getRatingsTriggersInputDisabled, getMaturityInputDisabled]);

    const calculatorInputsWidth = useMemo(() => (width / 2) - 6, [width]);
    const inputWrapperWidth = useMemo(() => `calc((100% / ${Math.floor(calculatorInputsWidth / 110)}) - 4px)`, [calculatorInputsWidth]);

    const showButtonWrapper = useMemo(() => calculatorFieldType.name !== LegacyCalculatorFieldType.CROSS_DEFAULT && (calculatorFieldType.includeIfElse || (!isNull(selectedBuilderIndex) && calculatorFieldType.includeConditionals)), [calculatorFieldType, selectedBuilderIndex]);
    const buttonWrapperStyle = useMemo(() => showButtonWrapper ? { borderTop: `2px solid ${grey}`, marginTop: '3px' } : {}, [showButtonWrapper]);

    const maxInputHeight = `${maxHeight - 55}px`;

    return (
        <div className={styles.editorWrapper} style={{ maxHeight: `${maxHeight - 2}px` }}>
            <Scrollable maxHeight={maxInputHeight}>
                <div className={styles.inputItemsWrapper}>
                    {availableInputs.map((input, index) => {
                        const inputDisabled = getInputDisabled(input);
                        return (
                            <CalculatorPadOption calculatorPadOption={input} disabled={inputDisabled || disabled} addOption={addOption} inputWrapperWidth={inputWrapperWidth} key={index} />
                        );
                    })}
                </div>
            </Scrollable>
            <div className={styles.buttonWrapper} style={buttonWrapperStyle}>
                {calculatorFieldType.includeIfElse === 1 &&
                    <CustomTooltip overlayText='Represent alternative outcomes by inserting an “If…Else…” statement into the calculation field, enabling you to represent multiple outcomes.' placement='left'>
                        <button className={styles.conditionButton} onClick={addNewCalculation}>IF...ELSE...</button>
                    </CustomTooltip>
                }
                {!isNull(selectedBuilderIndex) && calculatorFieldType.includeConditionals === 1 &&
                    <CustomTooltip overlayText='Link together the statements that you have constructed by adding an “and” or an “or” conjunction BETWEEN statements.'>
                        <button className={styles.conditionButton} onClick={addNewBuilder}>Add Conjunction</button>
                    </CustomTooltip>
                }
            </div>
        </div>
    );
};
